import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Stack, SvgIconProps, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { WasteManifestSource } from "generated-graphql/graphql";
import { ComponentType } from "react";

import { ManifestDataSources } from "./ManifestDataSources";
import { SectionHeader } from "./SectionHeader";
import { StatusLevel, SummaryItem } from "./summaryStatus";

/** Encircled bullet icon + color per status level. */
const LEVEL_ICON: Record<StatusLevel, { Icon: ComponentType<SvgIconProps>; color: string }> = {
  ok: { Icon: CheckCircle, color: "success.main" },
  warning: { Icon: ErrorIcon, color: "warning.main" },
  error: { Icon: ErrorIcon, color: "error.main" },
  info: { Icon: InfoIcon, color: "info.main" },
  neutral: { Icon: InfoIcon, color: "text.disabled" },
};

interface SummarySectionProps {
  items: SummaryItem[];
  /** When provided, renders the Data Sources chip panel as a right-hand column. */
  availableSources?: readonly WasteManifestSource[];
}

/**
 * At-a-glance manifest health as a human-readable, icon-bulleted list, with the
 * Data Sources chip panel as a second column on the right (headers aligned at
 * the top). Every line restates something available in detail elsewhere in the
 * drawer; this is the quick read. Source-agnostic.
 */
export function SummarySection({ items, availableSources }: SummarySectionProps) {
  if (items.length === 0 && !availableSources) return null;

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <SectionHeader title="Summary" />
        <Stack spacing={0.75}>
          {items.map((item) => {
            const { Icon, color } = LEVEL_ICON[item.level];
            return (
              <Stack key={item.key} direction="row" spacing={1} alignItems="center">
                <Icon sx={{ fontSize: "1.125rem", color, flexShrink: 0 }} />
                <Typography variant="body2">{item.message}</Typography>
                {item.tooltip && (
                  <InfoIconTooltip tooltip={item.tooltip} fontSize="1rem" iconButtonSx={{ p: 0 }} />
                )}
              </Stack>
            );
          })}
        </Stack>
      </Box>
      {availableSources && (
        <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: "20%" } }}>
          <ManifestDataSources availableSources={availableSources} />
        </Box>
      )}
    </Stack>
  );
}
